import React from "react";
import { X } from "lucide-react";

export const Button = ({ variant = "primary", className = "", children, ...p }) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg font-semibold text-sm px-4 py-2.5 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]";
  const variants = {
    primary: "bg-amber-500 text-slate-950 hover:bg-amber-400 shadow-sm shadow-amber-500/20",
    outline: "border border-slate-700 text-slate-200 hover:border-amber-500 hover:text-amber-400 bg-slate-900/40",
    ghost: "text-slate-300 hover:text-white hover:bg-slate-800",
    danger: "bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20",
    dark: "bg-slate-800 text-white hover:bg-slate-700 border border-slate-700",
  };
  return (
    <button className={`${base} ${variants[variant]} ${className}`} {...p}>
      {children}
    </button>
  );
};

export const Label = ({ children, className = "" }) => (
  <label className={`block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5 ${className}`}>
    {children}
  </label>
);

const fieldCls =
  "w-full rounded-lg bg-slate-950/60 border border-slate-700 px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-500 outline-none transition-colors focus:border-amber-500 focus:ring-1 focus:ring-amber-500/40";

export const Input = ({ className = "", ...p }) => <input className={`${fieldCls} ${className}`} {...p} />;
export const Textarea = ({ className = "", ...p }) => <textarea className={`${fieldCls} ${className}`} {...p} />;
export const Select = ({ className = "", children, ...p }) => (
  <select className={`${fieldCls} ${className}`} {...p}>{children}</select>
);

export const Card = ({ className = "", children, ...p }) => (
  <div className={`rounded-xl bg-slate-900/60 border border-slate-800 ${className}`} {...p}>{children}</div>
);

export const StatusPill = ({ status }) => {
  const map = {
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    signed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    invoiced: "bg-sky-500/10 text-sky-400 border-sky-500/30",
    active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  };
  const labels = { pending: "Väntar", signed: "Signerad", invoiced: "Fakturerad", active: "Aktiv" };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${map[status] || "bg-slate-700/30 text-slate-300 border-slate-600"}`}>
      {labels[status] || status}
    </span>
  );
};

export function Modal({ open, onClose, title, children, wide = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/80 backdrop-blur-sm p-4 sm:p-8" onClick={onClose}>
      <div
        className={`relative w-full ${wide ? "max-w-3xl" : "max-w-lg"} rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl rise`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <h3 className="font-display text-lg font-bold text-white">{title}</h3>
          <button data-testid="modal-close-button" onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}
